import { createServerFn } from "@tanstack/react-start";



export type GoogleReview = {
  name: string;
  rating: 1 | 2 | 3 | 4 | 5;
  publishedAt: string;
  date: string;
  text: string;
};

export type GoogleReviewsData = {
  reviews: GoogleReview[];
  avgRating: number;
  totalRatings: number;
  source: "live" | "fallback";
};

const PLACE_ID = "ChIJnXkX1OsFDaoRpNAmMO-fbUs";

// Curated reviews copied from the Google Business Profile. The Google API only
// returns up to 5 reviews per request, so these fill out the carousel and are
// merged with (and de-duplicated against) whatever the API returns live.
const FALLBACK_REVIEWS: GoogleReview[] = [
  {
    name: "Ethan England",
    rating: 5,
    publishedAt: "2026-08-31",
    date: "2 weeks ago",
    text: "They did a wonderful service and made sure they did an exceedingly great job before leaving. I would highly recommend to anyone in Lyneham / Wiltshire area.",
  },
  {
    name: "Lois Parry",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "Very friendly and reliable service - highly recommend this cleaning company! The clean was very well done, thorough and got all the hard to reach bits of the flat. I was really impressed by how the flat looked by the end, my bathroom has never looked cleaner! The most impressive thing was their professionalism and commitment to delivering outstanding service. Diana was very friendly and a lovely person to have around.",
  },
  {
    name: "Jenny Fowler",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "I contacted IZHUB Ltd for a house clean with not much notice as I am recovering from an operation and couldn't clean the house myself. They fitted me in for a one off clean and I am very impressed with the standard of clean. The ladies were lovely and fitted as much as they could into the 4 hours I booked. I definitely recommend and would have them back again in the future. Thank you.",
  },
  {
    name: "Natalie Vasey",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "We had our first clean on Friday. Emma's attention to detail was fantastic. She has done such a good job. Thank you so much.",
  },
  {
    name: "Sara Benfield",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "Excellent service, with a great eye for detail.",
  },
  {
    name: "jball2",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "Emma and her team do a great job. I would gladly recommend.",
  },
  {
    name: "Heather Charlesworth",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "Great communication from first contact. So amazing to come home to a spotless house and smells amazing. My children were so excited by all their teddies set up on their beds.",
  },
  {
    name: "Lisa Francis",
    rating: 5,
    publishedAt: "2026-08-14",
    date: "a month ago",
    text: "Emma and her team have cleaned my house twice now. I would highly recommend. I have a sigh of relief when I see how clean and shiny they leave things!",
  },
  {
    name: "Ria Rylatt-Chandaman",
    rating: 5,
    publishedAt: "2026-07-14",
    date: "2 months ago",
    text: "Great experience - a hard working team even in the hot heat!!",
  },
  {
    name: "hajer tzy",
    rating: 5,
    publishedAt: "2026-07-14",
    date: "2 months ago",
    text: "Miracle workers! Very attentive to detail and helpful. I will definitely be recommending, they are the best around.",
  },
  {
    name: "Galgenius",
    rating: 5,
    publishedAt: "2026-06-17",
    date: "3 months ago",
    text: "Emma is always available when I need her for her cleaning services. She takes her job seriously and ensures the job is done to my taste :)",
  },
];

function relativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const days = Math.max(1, Math.floor((now - then) / (1000 * 60 * 60 * 24)));
  if (days < 7) return days === 1 ? "a day ago" : `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return w === 1 ? "a week ago" : `${w} weeks ago`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return m === 1 ? "a month ago" : `${m} months ago`;
  }
  const y = Math.floor(days / 365);
  return y === 1 ? "a year ago" : `${y} years ago`;
}

export const getGoogleReviews = createServerFn({ method: "GET" }).handler(
  async (): Promise<GoogleReviewsData> => {

    const key = process.env.GOOGLE_API_KEY;
    if (!key) {
      return {
        reviews: FALLBACK_REVIEWS,
        avgRating: 5,
        totalRatings: FALLBACK_REVIEWS.length,
        source: "fallback",
      };
    }


    try {
      const res = await fetch(
        `https://places.googleapis.com/v1/places/${PLACE_ID}?languageCode=en&reviews_sort=newest&_ts=${Date.now()}`,
        {
          headers: {
            "X-Goog-Api-Key": key,
            "X-Goog-FieldMask": "reviews,rating,userRatingCount",
          },
          cache: "no-store",
        },
      );


      if (!res.ok) {
        console.error("Google Places API error", res.status, await res.text());
        return {
          reviews: FALLBACK_REVIEWS,
          avgRating: 5,
          totalRatings: FALLBACK_REVIEWS.length,
          source: "fallback",
        };
      }

      const json = (await res.json()) as {
        rating?: number;
        userRatingCount?: number;
        reviews?: Array<{
          rating: number;
          text?: { text?: string };
          originalText?: { text?: string };
          authorAttribution?: { displayName?: string };
          publishTime?: string;
          relativePublishTimeDescription?: string;
        }>;
      };

      const reviews: GoogleReview[] = (json.reviews ?? [])
        .filter((r) => r.rating === 5)
        .map((r) => {
          const publishedAt = r.publishTime ?? new Date().toISOString();
          return {
            name: r.authorAttribution?.displayName ?? "Google user",
            rating: 5 as const,
            publishedAt,
            date: r.relativePublishTimeDescription ?? relativeDate(publishedAt),
            text:
              r.text?.text ??
              r.originalText?.text ??
              "",
          };
        })
        .filter((r) => r.text.length > 0)
        .sort(
          (a, b) =>
            new Date(b.publishedAt).getTime() -
            new Date(a.publishedAt).getTime(),
        );

      if (reviews.length === 0) {
        return {
          reviews: FALLBACK_REVIEWS,
          avgRating: json.rating ?? 5,
          totalRatings: json.userRatingCount ?? FALLBACK_REVIEWS.length,
          source: "fallback",
        };
      }

      // Google returns at most 5 reviews per request — top up with curated
      // ones from the profile that the API did not return.
      const seen = new Set(reviews.map((r) => r.text.slice(0, 40).toLowerCase()));
      const merged = [
        ...reviews,
        ...FALLBACK_REVIEWS.filter(
          (r) => !seen.has(r.text.slice(0, 40).toLowerCase()),
        ),
      ];

      return {
        reviews: merged,
        avgRating: json.rating ?? 5,
        totalRatings: json.userRatingCount ?? merged.length,
        source: "live",
      };
    } catch (err) {
      console.error("Google reviews fetch failed", err);
      return {
        reviews: FALLBACK_REVIEWS,
        avgRating: 5,
        totalRatings: FALLBACK_REVIEWS.length,
        source: "fallback",
      };
    }
  },
);
